/**
 * Cart drawer — gift selector safety + refresh.
 *
 * Runs after cart-drawer-section-sync.js (so fetchCartDrawerSectionHtml,
 * clearCartDrawerGiftChooserTakeover, qualifyingSpendCentsFromCart, and the
 * shared _cartDrawerSectionHtmlPromise are in realm scope).
 *
 * Responsibilities:
 *   • Keep <cart-drawer-gift-selector> permanent: if anything removes it,
 *     the safety observer fetches a fresh cart-drawer section render and
 *     re-injects the selector in its canonical position.
 *   • Serve as the primary cart:updated + cart:mutated listener — drops
 *     the coalesced section-fetch promise on every mutation so the very
 *     next caller always triggers a fresh request.
 *   • Auto-remove a claimed gift line when the customer falls under the
 *     tier threshold (opt-in; off by default).
 */

/* ──────────────────────────────────────────────────────────────────────────
   Permanent gift-selector safety net.

   Bug class: any code path (ours, a third-party app, a Shopify SDK update,
   a partial AJAX response, a race between two concurrent /cart fetches)
   that accidentally removes <cart-drawer-gift-selector> from the live DOM
   used to result in "the gift banner just disappears". The merchant has
   explicitly opted in to a permanent banner — so instead of trying to find
   every possible offender, we install a single MutationObserver on
   #CartDrawer .drawer__inner that watches for the selector going missing
   and immediately re-fetches+re-injects from `?section_id=cart-drawer`.

   Behavior:
   • Only arms when the merchant has gift selector enabled (data-cfg-gift).
   • Debounced — bursts of mutations during a section swap collapse to a
     single re-check on the next animation frame.
   • Idempotent — if the selector is present after the swap, nothing happens.
   • Cheap — childList + subtree on a small subtree (the drawer body).
   • Self-pausing during refreshGiftSelectorFromSection so its own
     replaceWith() doesn't trigger a pointless re-fetch loop.
   ────────────────────────────────────────────────────────────────────── */
let _giftSafetyObserver = null;
let _giftSafetyPaused = false;
let _giftSafetyArmed = false;
let _giftSafetyRecheckScheduled = false;

function pauseGiftSafetyObserver(fn) {
  _giftSafetyPaused = true;
  try { return fn(); } finally {
    requestAnimationFrame(() => { _giftSafetyPaused = false; });
  }
}

function scheduleGiftSafetyRecheck() {
  if (_giftSafetyRecheckScheduled) return;
  _giftSafetyRecheckScheduled = true;
  requestAnimationFrame(() => {
    _giftSafetyRecheckScheduled = false;
    if (_giftSafetyPaused) return;
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    if (drawer.getAttribute('data-cfg-gift') !== 'true') return;
    // Either gift element being present means the system is intact. In
    // auto mode the manual <cart-drawer-gift-selector> is never rendered;
    // checking only for it would fire a wasted /cart-drawer section fetch
    // on every cart:updated when auto mode is active. cart-drawer.js owns
    // the auto-gift snapshot/restore, so this safety net is manual-mode
    // only.
    if (drawer.querySelector('cart-drawer-gift-selector')) return;
    if (drawer.querySelector('cart-drawer-auto-gift')) return;
    refreshGiftSelectorFromSection();
  });
}

function armGiftSafetyObserver() {
  if (_giftSafetyArmed) return;
  if (typeof MutationObserver === 'undefined') return;
  const drawer = document.getElementById('CartDrawer');
  const inner = drawer && drawer.querySelector('.drawer__inner');
  if (!inner) return;

  _giftSafetyObserver = new MutationObserver((mutations) => {
    if (_giftSafetyPaused) return;
    if (drawer.getAttribute('data-cfg-gift') !== 'true') return;
    for (const m of mutations) {
      if (!m.removedNodes || !m.removedNodes.length) continue;
      for (const node of m.removedNodes) {
        if (!(node instanceof Element)) continue;
        const tag = node.tagName;
        if (
          tag === 'CART-DRAWER-GIFT-SELECTOR' ||
          tag === 'CART-DRAWER-AUTO-GIFT' ||
          node.querySelector?.('cart-drawer-gift-selector, cart-drawer-auto-gift')
        ) {
          scheduleGiftSafetyRecheck();
          return;
        }
      }
    }
  });

  _giftSafetyObserver.observe(inner, { childList: true, subtree: true });
  _giftSafetyArmed = true;
}

if (typeof document !== 'undefined') {
  /*
    Every init call is wrapped so a thrown error from one sub-feature
    (observer construction, DOM query, etc.) can't break the rest of the
    cart boot path. The pattern: catch → console.warn → keep going.
  */
  const _safeArmGiftSafetyObserver = () => {
    try { armGiftSafetyObserver(); } catch (err) { console.warn('[cart] armGiftSafetyObserver failed', err); }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _safeArmGiftSafetyObserver);
  } else {
    _safeArmGiftSafetyObserver();
  }
  /* Re-arm after every cart event in case .drawer__inner itself was
     replaced (which would orphan the previous observer). Wrapped end-to-end
     so a failure here never blocks future cart:updated dispatches — this
     listener is the primary cache-invalidation hook and losing it would
     leave every gift-selector in the tab reading stale totals. */
  document.addEventListener('cart:updated', (e) => {
    try {
      _giftSafetyArmed = false;
      _giftSafetyObserver?.disconnect?.();
      _safeArmGiftSafetyObserver();
      scheduleGiftSafetyRecheck();

      /* Any coalesced `?section_id=cart-drawer` request that was in flight
         before this mutation fired is no longer trustworthy — the HTML it
         returns may be computed against the pre-mutation cart. Drop the
         cached promise so the very next caller fires a fresh request. */
      _cartDrawerSectionHtmlPromise = null;
    } catch (err) {
      console.warn('[cart] cart:updated primary handler failed', err);
    }

    /* Authoritative cart-total cache. Lives on `window` so ANY gift selector
       instance — including ones that mount AFTER this event (async section
       fetch replaceWith, defensive snapshot restore, quick-add, etc.) — can
       self-reconcile its "Spend $X more" copy against the freshest known
       total during its own connectedCallback. Kept in its own try so a
       malformed cartData payload can't kill the handler above. */
    try {
      const cd = e && e.detail && e.detail.cartData;
      if (cd) {
        const hasPrice =
          typeof cd.total_price === 'number' ||
          typeof cd.items_subtotal_price === 'number';
        const hasLineItems = Array.isArray(cd.items);
        const lineCount = hasLineItems ? cd.items.length : 0;
        const reportedItems = typeof cd.item_count === 'number' ? cd.item_count : null;
        const payloadLineItemsComplete = hasLineItems && !(lineCount === 0 && reportedItems > 0);

        if (!hasPrice || !payloadLineItemsComplete) return;
        window.__lastCartTotalCents = qualifyingSpendCentsFromCart(cd);
      }
    } catch (_) {}
  });

  /* Additional invalidation hook: any code path that knows a mutation is
     about to commit can fire `cart:mutated` to preemptively drop the shared
     section-HTML promise before `cart:updated` lands. Safe no-op if no one
     dispatches it. */
  document.addEventListener('cart:mutated', () => {
    _cartDrawerSectionHtmlPromise = null;
  });
}

let _giftRefreshInFlight = false;
const GIFT_REFRESH_MAX_RETRIES = 6; // ~720ms total backoff ceiling at 120ms
function refreshGiftSelectorFromSection(_retryCount) {
  if (typeof routes === 'undefined' || !routes.cart_url) return Promise.resolve();
  const retry = typeof _retryCount === 'number' ? _retryCount : 0;
  if (_giftRefreshInFlight) {
    if (retry >= GIFT_REFRESH_MAX_RETRIES) {
      // Give up on this call rather than stacking more setTimeouts indefinitely —
      // the in-flight request will complete and the next legitimate trigger can
      // retry. Prevents an unbounded retry chain on a silent/stuck fetch.
      return Promise.resolve();
    }
    // Preserve retry semantics but surface them as a Promise so callers can
    // await the eventual DOM swap.
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(refreshGiftSelectorFromSection(retry + 1));
      }, 120);
    });
  }
  _giftRefreshInFlight = true;

  // Hard safety timeout: if the fetch/parse never resolves (very slow network,
  // aborted request), clear the in-flight flag so subsequent triggers are not
  // permanently blocked.
  let safetyTimer = setTimeout(() => {
    if (window.DEBUG_CART) {
      console.warn('[cart] refreshGiftSelectorFromSection: safety timer force-cleared _giftRefreshInFlight after 3000ms');
    }
    _giftRefreshInFlight = false;
  }, 3000);

  return fetchCartDrawerSectionHtml()
    .then((html) => {
      clearTimeout(safetyTimer);
      _giftRefreshInFlight = false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const drawer = document.getElementById('CartDrawer');
      if (!drawer) return;

      // Manual selector OR auto-gift controller — both legitimate. Auto-mode
      // markup contains <cart-drawer-auto-gift>; bailing on a missing manual
      // selector here used to skip auto-mode recovery entirely.
      const sourceGift =
        doc.querySelector('cart-drawer-gift-selector') ||
        doc.querySelector('cart-drawer-auto-gift');
      if (!sourceGift) return;

      pauseGiftSafetyObserver(() => {
        const targetGift =
          drawer.querySelector('cart-drawer-gift-selector') ||
          drawer.querySelector('cart-drawer-auto-gift');
        if (targetGift) {
          targetGift.replaceWith(sourceGift);
        } else {
          // Insert inside .cart-drawer__footer right above the order summary so
          // the selector always sits directly on top of the estimated total.
          const innerFooter = drawer.querySelector('.cart-drawer__footer');
          if (innerFooter) {
            const orderSummary = innerFooter.querySelector('.cart-drawer__order-summary');
            if (orderSummary) {
              innerFooter.insertBefore(sourceGift, orderSummary);
            } else {
              innerFooter.insertBefore(sourceGift, innerFooter.firstChild);
            }
          } else {
            const inner = drawer.querySelector('.drawer__inner');
            if (inner) inner.appendChild(sourceGift);
          }
        }

        clearCartDrawerGiftChooserTakeover();
        document.querySelectorAll('#CartDrawer cart-drawer-gift-selector').forEach((el) => {
          el.classList.remove('gift-chooser-mode');
          el._giftUiBound = false;
          if (typeof el._initUI === 'function') el._initUI();
          if (el.dataset.useCarousel === 'true' && typeof window.initGiftTierCarousel === 'function') {
            window.initGiftTierCarousel(el);
          }
          // Belt-and-suspenders: even though the server-rendered selector
          // should be computed against the live cart, a cached Section Render
          // API response or a race between this fetch and the cart mutation
          // can leave the locked CTA showing stale "Spend $X more" text. Force
          // a recompute against the most recently observed total so the
          // replacement never shows a number older than what the customer
          // just did.
          const cached = window.__lastCartTotalCents;
          if (typeof cached === 'number' && typeof el._recomputeTierEligibility === 'function') {
            try { el._recomputeTierEligibility(cached); } catch (_) {}
          }
        });
      });
    })
    .catch(() => { clearTimeout(safetyTimer); _giftRefreshInFlight = false; });
}

/** Re-run gift selector init on existing nodes (DOM was not replaced). */
function reinitCartDrawerGiftSelectors() {
  document.querySelectorAll('#CartDrawer cart-drawer-gift-selector').forEach((el) => {
    if (typeof el._initUI === 'function') el._initUI();
    if (el.dataset.useCarousel === 'true' && typeof window.refreshGiftTierCarousel === 'function') {
      try {
        window.refreshGiftTierCarousel(el);
      } catch (_) {}
    }
  });
}

/**
 * Cart change requests only re-render getSectionsToRender() — not the cart-drawer section.
 * The free-gift block stays stale (e.g. still "claimed" after removing the gift line).
 * Fetch fresh cart-drawer HTML when the gift selector is enabled.
 */
function syncCartDrawerGiftSelectorAfterLineChange() {
  const drawer = document.getElementById('CartDrawer');
  if (drawer && drawer.getAttribute('data-cfg-gift') === 'true') {
    refreshGiftSelectorFromSection();
    return;
  }
  reinitCartDrawerGiftSelectors();
}

/**
 * If a gift item is in the cart but the customer no longer meets the
 * threshold, automatically remove the gift line item.
 */
let _giftRemovalInFlight = false;
function enforceGiftThreshold(parsedState) {
  if (_giftRemovalInFlight) return;
  if (!parsedState || !parsedState.items) return;

  const giftEl = document.querySelector('#CartDrawer cart-drawer-gift-selector');
  if (!giftEl) return;

  /* Auto-removal is OPT-IN. By default we never strip a claimed gift line,
     because that fires a destructive `cartDrawer.renderContents()` which can
     wipe the gift selector entirely if the new server HTML is missing it.
     Merchants who *want* the legacy "drop the gift if you fall under the
     threshold" behavior can opt in with `data-auto-remove="true"` on the
     gift selector element. Without the flag, the gift line stays put and the
     CTA copy updates instead — matching the user's stated intent of
     "the gift selector should be permanent, only the buttons change". */
  if (giftEl.dataset.autoRemove !== 'true') return;

  const blockId = giftEl.dataset.blockId || '';
  if (!blockId) return;

  const failingLines = [];
  const qualifying = qualifyingSpendCentsFromCart(parsedState);
  for (let i = 0; i < parsedState.items.length; i++) {
    const props = parsedState.items[i].properties || {};
    if (props['_drawer_gift_selector'] !== blockId) continue;
    const tierIdx = String(props['_drawer_gift_tier_index'] || '1');
    const slide = giftEl.querySelector(`[data-gift-tier-slide][data-tier-index="${tierIdx}"]`);
    const thresholdCents = slide
      ? parseInt(slide.dataset.tierThresholdCents, 10) || 0
      : parseInt(giftEl.dataset.thresholdCents, 10) || 0;
    if (thresholdCents <= 0) continue;
    if (qualifying < thresholdCents) {
      failingLines.push(i + 1);
    }
  }
  if (failingLines.length === 0) return;

  failingLines.sort((a, b) => b - a);
  const giftLineIndex = failingLines[0] - 1;

  _giftRemovalInFlight = true;
  const cartDrawer = document.querySelector('cart-drawer');
  const sections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
    ? cartDrawer.getSectionsToRender().map((s) => s.id || s.section).filter(Boolean)
    : [];

  const body = JSON.stringify({
    line: giftLineIndex + 1,
    quantity: 0,
    sections: sections,
    sections_url: window.location.pathname,
  });

  fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
    .then((r) => r.text())
    .then((text) => {
      _giftRemovalInFlight = false;
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        console.error('[cart] parse failed', err);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors && window.cartStrings && window.cartStrings.error) {
          errors.textContent = window.cartStrings.error;
        }
        return;
      }
      if (cartDrawer) {
        cartDrawer.renderContents(parsed);
      } else {
        window.location.reload();
      }
    })
    .catch(() => { _giftRemovalInFlight = false; });
}
